import { useState, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Database, TrendingUp, Clock, Hash, BarChart3, MapPin, Download, Loader2 } from 'lucide-react';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { toPng } from 'html-to-image';
import MapEditor from '../components/maps/MapEditor';
import api from '../lib/api';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { useAuth } from '../lib/auth';

interface DataField {
  id: string;
  name: string;
  type: string;
  aggregationMode?: string;
  hasPersonReference?: boolean;
  isSensitive?: boolean;
}

interface DataEntry {
  id: string;
  dataFieldId: string;
  dataField: DataField;
  value: string;
  sourceType?: string;
  sourceId?: string;
  timestamp: string;
  createdBy?: string;
  isLate?: boolean;
}

interface AggregatedEntry {
  dataField: DataField;
  dataFieldId: string;
  latestValue: string;
  latestTimestamp: string;
  sum: number;
  count: number;
  values: string[];
}

interface HistoryEntry {
  dataFieldId: string;
  dataField: { id: string; name: string; type: string; aggregationMode: string; resetInterval: string };
  day: string;
  count: number;
  sum: number;
  lastValue: string;
  lastTimestamp: string;
}

interface ValueAtResult {
  fieldId: string;
  field: { name: string; type: string; aggregationMode: string; resetInterval: string };
  date: string;
  value: string;
  count: number;
  mode: string;
  timestamp: string | null;
}

const sourceTypeLabels: Record<string, string> = {
  api: 'API',
  import: 'Import',
  manual: 'Manuell',
  mapping: 'Verarbeitungslogik',
  submission: 'Einreichung',
};

const sourceTypeColors: Record<string, string> = {
  api: 'bg-blue-100 text-blue-700',
  import: 'bg-purple-100 text-purple-700',
  manual: 'bg-green-100 text-green-700',
  mapping: 'bg-orange-100 text-orange-700',
  submission: 'bg-emerald-100 text-emerald-700',
};

function extractValue(raw: string | undefined | null): string {
  if (!raw) return '—';
  // Aggregierte JSON-Wrapper ({value:"300", sourceCount:2, ...}) extrahieren
  if (raw.startsWith('{') && (raw.includes('"value"') || raw.includes("'value'"))) {
    try {
      const p = JSON.parse(raw);
      if (p.value !== undefined) {
        return String(p.value);
      }
    } catch { /* ignore */ }
  }
  return raw;
}
function formatPoiEntry(p: any): string {
  const parts: string[] = [];
  if (p.name) parts.push(p.name);
  if (p.description) parts.push(p.description);
  if (p.lat !== 0 || p.lon !== 0) parts.push('(' + (p.lat || 0) + ', ' + (p.lon || 0) + ')');
  return parts.join(' - ') || 'Unbenannter Punkt';
}

function FormatPoiList({ value, navigate }: { value: string, navigate: (url: string) => void }) {
  try {
    const arr = JSON.parse(value);
    if (!Array.isArray(arr) || arr.length === 0) return <span className="text-xs text-gray-400">&mdash;</span>;
    return (
      <div className="space-y-1 max-w-xs">
        {arr.map((p: any, i: number) => (
          <div key={i} className="flex items-center gap-1.5 text-xs">
            <span className="text-gray-800 truncate">{formatPoiEntry(p)}</span>
            {(p.lat !== 0 || p.lon !== 0) && (
              <button
                onClick={(e) => { e.stopPropagation(); navigate('/maps?lat=' + (p.lat || 0) + '&lon=' + (p.lon || 0)); }}
                className="text-blue-500 hover:text-blue-700 p-0.5 shrink-0"
                title="Auf Karte anzeigen"
              >
                <MapPin size={12} />
              </button>
            )}
          </div>
        ))}
      </div>
    );
  } catch { return <span className="text-xs text-gray-800 truncate">{value}</span>; }
}

function FormatPoi({ value, navigate }: { value: string, navigate: (url: string) => void }) {
  try {
    const p = JSON.parse(value);
    if (!p || typeof p !== 'object') return <span className="text-xs text-gray-800 truncate">{value}</span>;
    return (
      <div className="flex items-center gap-1.5 text-xs">
        <span className="text-gray-800 truncate">{formatPoiEntry(p)}</span>
        {(p.lat !== 0 || p.lon !== 0) && (
          <button
            onClick={(e) => { e.stopPropagation(); navigate('/maps?lat=' + (p.lat || 0) + '&lon=' + (p.lon || 0)); }}
            className="text-blue-500 hover:text-blue-700 p-0.5 shrink-0"
            title="Auf Karte anzeigen"
          >
            <MapPin size={12} />
          </button>
        )}
      </div>
    );
  } catch { return <span className="text-xs text-gray-800 truncate">{value}</span>; }
}



export default function DataPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState<'entries' | 'aggregated' | 'history'>('aggregated');
  const [sourceFilter, setSourceFilter] = useState('');
  const [orgFilter, setOrgFilter] = useState('');
  const [historyDateFrom, setHistoryDateFrom] = useState(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10));
  const [historyDateTo, setHistoryDateTo] = useState(new Date().toISOString().slice(0, 10));
  const [historyFieldFilter, setHistoryFieldFilter] = useState('');
  const [historyChartField, setHistoryChartField] = useState('');
  const [historyChartType, setHistoryChartType] = useState<'bar' | 'line'>('bar');
  const chartExportRef = useRef<HTMLDivElement>(null);

  const exportChartAsImage = async () => {
    if (!chartExportRef.current) return;
    try {
      const dataUrl = await toPng(chartExportRef.current, { backgroundColor: '#ffffff' });
      const link = window.document.createElement('a');
      const fieldName = (history || []).find((h: any) => h.dataFieldId === historyChartField)?.dataField?.name || 'chart';
      link.download = `${fieldName}-verlauf-${historyDateFrom || 'start'}-${historyDateTo || 'end'}.png`;
      link.href = dataUrl;
      link.click();
    } catch (err) {
      console.error('Chart export failed:', err);
    }
  };

  const { data: orgsForFilter } = useQuery<any>({ queryKey: ['organizations-filter'], queryFn: async () => { const res = await api.get('/organizations', { params: { limit: 200 } }); return res.data; } });

  const { data: entries, isLoading, error } = useQuery<DataEntry[]>({
    queryKey: ['data-entries', sourceFilter, orgFilter],
    queryFn: async () => {
      const params: any = { limit: 100 };
      if (sourceFilter) params.sourceType = sourceFilter;
      if (orgFilter) params.organizationId = orgFilter;
      const res = await api.get('/data-fields/entries/all', { params });
      return res.data;
    },
  });

  const { data: aggregated } = useQuery<AggregatedEntry[]>({
    queryKey: ['data-entries-aggregated'],
    queryFn: async () => {
      const res = await api.get('/data-fields/entries/aggregated');
      return res.data;
    },
    enabled: tab === 'aggregated',
  });

  const { data: history, isLoading: historyLoading } = useQuery<HistoryEntry[]>({
    queryKey: ['data-field-history', historyDateFrom, historyDateTo, historyFieldFilter],
    queryFn: async () => {
      const params: any = {};
      if (historyDateFrom) params.from = historyDateFrom;
      if (historyDateTo) params.to = historyDateTo;
      if (historyFieldFilter) params.fieldId = historyFieldFilter;
      const res = await api.get('/data-fields/history', { params });
      return res.data;
    },
    enabled: tab === 'history',
  });

  const { data: fieldsForFilter } = useQuery<any[]>({
    queryKey: ['fields-list-filter'],
    queryFn: async () => {
      const res = await api.get('/data-fields', { params: { limit: 200 } });
      return Array.isArray(res.data) ? res.data : (res.data?.data ?? []);
    },
    enabled: tab === 'history',
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="card text-center py-12">
        <p className="text-red-500">Fehler beim Laden: {(error as Error).message}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Database size={28} className="text-primary" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Daten & Einreichungen</h1>
            <p className="text-sm text-gray-500">Alle erfassten Daten aus APIs, Importen und Fragebögen</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-200">
        <button
          onClick={() => setTab('entries')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            tab === 'entries' ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <Clock size={14} className="inline mr-1.5" />
          Einzelwerte ({entries?.length || 0})
        </button>
        <button
          onClick={() => setTab('aggregated')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            tab === 'aggregated' ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <BarChart3 size={14} className="inline mr-1.5" />
          Aggregiert ({aggregated?.length || 0})
        </button>
        <button
          onClick={() => setTab('history')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            tab === 'history' ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <Clock size={14} className="inline mr-1.5" />
          Verlauf
        </button>
      </div>

      {/* Filter */}
      {tab === 'entries' && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-gray-500">Quelle:</span>
          {['', 'api', 'import', 'mapping', 'submission'].map((s) => (
            <button
              key={s}
              onClick={() => setSourceFilter(s)}
              className={`text-xs px-2.5 py-1 rounded-full transition-colors ${
                sourceFilter === s
                  ? 'bg-primary text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {s ? sourceTypeLabels[s] || s : 'Alle'}
            </button>
          ))}
          <span className="text-xs text-gray-400 mx-1">|</span>
          <span className="text-xs text-gray-500">Org:</span>
          <select
            className="text-xs border rounded px-2 py-1 bg-white"
            value={orgFilter}
            onChange={(e) => setOrgFilter(e.target.value)}
          >
            <option value="">Alle Organisationen</option>
            {(Array.isArray(orgsForFilter) ? orgsForFilter : (orgsForFilter?.data ?? [])).map((org: any) => (
              <option key={org.id} value={org.id}>{org.name}</option>
            ))}
          </select>
        </div>
      )}

      {/* Entries Table */}
      {tab === 'entries' && (
        <div className="card overflow-hidden">
          {entries && entries.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50 text-left">
                    <th className="px-4 py-3 font-medium text-gray-600">Datenfeld</th>
                    <th className="px-4 py-3 font-medium text-gray-600">Typ</th>
                    <th className="px-4 py-3 font-medium text-gray-600">Wert</th>
                    <th className="px-4 py-3 font-medium text-gray-600">Quelle</th>
                    <th className="px-4 py-3 font-medium text-gray-600">Zeitpunkt</th>
                    <th className="px-4 py-3 font-medium text-gray-600">Ersteller</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {entries.map((entry) => (
                    <tr key={entry.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="font-medium text-gray-900">{entry.dataField?.name || '?'}</span>
                          {entry.dataField?.hasPersonReference && (
                            <span className="text-[10px] px-1 py-0.5 rounded bg-orange-100 text-orange-700 font-medium" title="Personenbezogene Daten">👤</span>
                          )}
                          {entry.dataField?.isSensitive && (
                            <span className="text-[10px] px-1 py-0.5 rounded bg-red-100 text-red-700 font-medium" title="Sensibel">🔒</span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="badge-info text-xs">{entry.dataField?.type}</span>
                        {entry.dataField?.aggregationMode === 'SUM' && (
                          <TrendingUp size={12} className="inline ml-1 text-accent-orange" title="SUM-Aggregation" />
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded max-w-xs truncate block">
                          {entry.dataField?.type === 'GEOJSON' ? (
                          <div className="max-w-xs">
                            <MapEditor value={entry.value} onChange={() => {}} readOnly height="150px" />
                          </div>
                        ) : entry.dataField?.type === 'COORDINATE_PAIR' && entry.value ? (
                            <button
                              onClick={() => navigate('/maps')}
                              className="text-xs bg-blue-50 text-blue-600 hover:bg-blue-100 px-1.5 py-0.5 rounded cursor-pointer"
                              title="Auf Karte anzeigen"
                            >
                              📍 {extractValue(entry.value)}
                            </button>
                          ) : entry.dataField?.type === 'POI_LIST' && entry.value ? (
                            <FormatPoiList value={extractValue(entry.value)} navigate={navigate} />
                          ) : entry.dataField?.type === 'POI' && entry.value ? (
                            <FormatPoi value={extractValue(entry.value)} navigate={navigate} />
                          ) : (
                            <span className="group relative">
                              {extractValue(entry.value)}
                              {entry.entryId && entry.sourceType === 'submission' && ((entries || []).filter((e: any) => e.dataFieldId === entry.dataFieldId && e.entryId === entry.entryId).length > 1) && (
                                <>
                                  <span className="ml-1 text-amber-500 text-xs" title="Wert wurde geändert">*</span>
                                  <div className="absolute bottom-full left-0 mb-1 hidden group-hover:block z-50">
                                    <div className="bg-gray-900 text-white text-xs rounded-lg px-3 py-2 shadow-lg whitespace-nowrap max-w-xs">
                                      <p className="font-medium mb-1">Historie:</p>
                                      {(entries || []).filter((e: any) => e.dataFieldId === entry.dataFieldId).sort((a: any, b: any) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()).slice(0, 10).map((e: any, i: number) => (
                                        <div key={i} className={e.id === entry.id ? 'text-green-300' : 'text-gray-400'}>
                                          {e.id === entry.id ? '↳ ' : '  '}{extractValue(e.value)} · {format(new Date(e.timestamp), 'HH:mm', { locale: de })}
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                </>
                              )}
                            </span>
                          )}
                        </code>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-1.5 py-0.5 rounded-full ${sourceTypeColors[entry.sourceType || ''] || 'bg-gray-100 text-gray-600'}`}>
                          {sourceTypeLabels[entry.sourceType || ''] || entry.sourceType || '—'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">
                        {format(new Date(entry.timestamp), 'dd.MM.yy HH:mm', { locale: de })}
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-400">
                        {entry.creatorName || entry.createdBy || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="py-12 text-center">
              <Database size={40} className="mx-auto text-gray-300 mb-2" />
              <p className="text-sm text-gray-500">Keine Dateneinträge vorhanden</p>
              <p className="text-xs text-gray-400 mt-1">
                Führe einen API-Endpunkt aus oder importiere Daten, um Einträge zu sehen
              </p>
            </div>
          )}
        </div>
      )}

      {/* Aggregated Table */}
      {tab === 'aggregated' && (
        <div className="card overflow-hidden">
          {aggregated && aggregated.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50 text-left">
                    <th className="px-4 py-3 font-medium text-gray-600">Datenfeld</th>
                    <th className="px-4 py-3 font-medium text-gray-600">Typ</th>
                    <th className="px-4 py-3 font-medium text-gray-600">Aggregation</th>
                    <th className="px-4 py-3 font-medium text-gray-600">Letzter Wert</th>
                    <th className="px-4 py-3 font-medium text-gray-600">Summe</th>
                    <th className="px-4 py-3 font-medium text-gray-600">Anzahl</th>
                    <th className="px-4 py-3 font-medium text-gray-600">Letzte Aktualisierung</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {aggregated.map((agg) => (
                    <tr key={agg.dataFieldId} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium text-gray-900">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span>{agg.dataField?.name || '?'}</span>
                          {agg.dataField?.hasPersonReference && (
                            <span className="text-[10px] px-1 py-0.5 rounded bg-orange-100 text-orange-700 font-medium" title="Personenbezogene Daten">👤</span>
                          )}
                          {agg.dataField?.isSensitive && (
                            <span className="text-[10px] px-1 py-0.5 rounded bg-red-100 text-red-700 font-medium" title="Sensibel">🔒</span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="badge-info text-xs">{agg.dataField?.type}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs">
                          {agg.dataField?.aggregationMode === 'SUM' ? (
                            <span className="badge-warning text-xs">SUM</span>
                          ) : (
                            <span className="badge-info text-xs">OVERWRITE</span>
                          )}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {agg.dataField?.type === 'POI_LIST' && agg.latestValue ? (
                            <FormatPoiList value={extractValue(agg.latestValue)} navigate={navigate} />
                        ) : agg.dataField?.type === 'POI' && agg.latestValue ? (
                            <FormatPoi value={extractValue(agg.latestValue)} navigate={navigate} />
                        ) : agg.dataField?.type === 'COORDINATE_PAIR' && agg.latestValue ? (
                          <button
                            onClick={() => navigate('/maps')}
                            className="text-xs bg-blue-50 text-blue-600 hover:bg-blue-100 px-1.5 py-0.5 rounded cursor-pointer"
                            title="Auf Karte anzeigen"
                          >
                            📍 {extractValue(agg.latestValue)}
                          </button>
                        ) : (
                          <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">{extractValue(agg.latestValue)}</code>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {agg.dataField?.aggregationMode === 'SUM' ? (
                          <span className="font-mono text-sm font-medium text-gray-900">{agg.sum.toLocaleString()}</span>
                        ) : (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500">{agg.count}</td>
                      <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">
                        {format(new Date(agg.latestTimestamp), 'dd.MM.yy HH:mm', { locale: de })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="py-12 text-center">
              <BarChart3 size={40} className="mx-auto text-gray-300 mb-2" />
              <p className="text-sm text-gray-500">Keine aggregierten Daten vorhanden</p>
            </div>
          )}
        </div>
      )}
      {/* History Tab */}
      {tab === 'history' && (
        <div className="space-y-4">
          <div className="flex items-center gap-3 flex-wrap">
            <div>
              <label className="text-xs text-gray-500 block mb-1">Von</label>
              <input type="date" className="input-field text-sm"
                value={historyDateFrom}
                onChange={(e) => setHistoryDateFrom(e.target.value)} />
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">Bis</label>
              <input type="date" className="input-field text-sm"
                value={historyDateTo}
                onChange={(e) => setHistoryDateTo(e.target.value)} />
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">Feld</label>
              <select className="input-field text-sm"
                value={historyFieldFilter}
                onChange={(e) => setHistoryFieldFilter(e.target.value)}>
                <option value="">Alle Felder</option>
                {(Array.isArray(fieldsForFilter) ? fieldsForFilter : []).map((f: any) => (
                  <option key={f.id} value={f.id}>{f.label || f.name}</option>
                ))}
              </select>
            </div>
            <div className="pt-5">
              <button
                onClick={async () => {
                  const params: any = {};
                  if (historyDateFrom) params.from = historyDateFrom;
                  if (historyDateTo) params.to = historyDateTo;
                  if (historyFieldFilter) params.fieldId = historyFieldFilter;
                  try {
                    const res = await api.get('/data-fields/history/export', { params, responseType: 'blob' });
                    const url = window.URL.createObjectURL(new Blob([res.data]));
                    const a = window.document.createElement('a');
                    a.href = url;
                    a.download = `zeitreihe-${historyDateFrom || 'start'}-${historyDateTo || 'end'}.csv`;
                    a.click();
                    window.URL.revokeObjectURL(url);
                  } catch {}
                }}
                className="btn-secondary text-xs px-3 py-2"
              >
                <Download size={14} className="mr-1" /> CSV exportieren
              </button>
            </div>
          </div>

          <div className="card overflow-hidden">
            {historyLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 size={24} className="animate-spin text-gray-400" />
              </div>
            ) : history && history.length > 0 ? (
              <div className="overflow-x-auto max-h-[70vh] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 bg-gray-50 text-left sticky top-0">
                      <th className="px-4 py-3 font-medium text-gray-600">Datenfeld</th>
                      <th className="px-4 py-3 font-medium text-gray-600">Typ</th>
                      <th className="px-4 py-3 font-medium text-gray-600">Datum</th>
                      <th className="px-4 py-3 font-medium text-gray-600">Tageswert</th>
                      <th className="px-4 py-3 font-medium text-gray-600">Summe</th>
                      <th className="px-4 py-3 font-medium text-gray-600">Anzahl</th>
                      <th className="px-4 py-3 font-medium text-gray-600">Aggregation</th>
                      <th className="px-4 py-3 font-medium text-gray-600">Reset</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {history.map((entry, idx) => (
                      <tr key={entry.dataFieldId + '-' + entry.day} className="hover:bg-gray-50">
                        <td className="px-4 py-2 text-xs font-medium text-gray-900">{entry.dataField?.name || '?'}</td>
                        <td className="px-4 py-2"><span className="badge-info text-[10px]">{entry.dataField?.type}</span></td>
                        <td className="px-4 py-2 text-xs text-gray-500 whitespace-nowrap">{entry.day}</td>
                        <td className="px-4 py-2">
                          <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">{entry.lastValue || '—'}</code>
                        </td>
                        <td className="px-4 py-2 text-xs font-mono">
                          {entry.dataField?.aggregationMode === 'SUM' ? (
                            <span className="font-medium">{entry.sum.toLocaleString()}</span>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-4 py-2 text-xs text-gray-500">{entry.count}</td>
                        <td className="px-4 py-2">
                          <span className={`text-[10px] px-1.5 py-0.5 rounded ${entry.dataField?.aggregationMode === 'SUM' ? 'badge-warning' : 'badge-info'}`}>
                            {entry.dataField?.aggregationMode || 'OVERWRITE'}
                          </span>
                        </td>
                        <td className="px-4 py-2">
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">
                            {{
                              NEVER: 'Nie',
                              DAILY: 'Täglich',
                              WEEKLY: 'Wöchentlich',
                              MONTHLY: 'Monatlich',
                              YEARLY: 'Jährlich',
                            }[entry.dataField?.resetInterval || 'NEVER'] || entry.dataField?.resetInterval || 'Nie'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="py-12 text-center">
                <Clock size={40} className="mx-auto text-gray-300 mb-2" />
                <p className="text-sm text-gray-500">Keine Verlaufsdaten für den gewählten Zeitraum</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* History Chart */}
      {tab === 'history' && history && history.length > 0 && (
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-700">Verlauf als Diagramm</h3>
            <div className="flex items-center gap-2">
              <div className="bg-gray-100 rounded-lg p-0.5 flex">
                <button
                  onClick={() => setHistoryChartType('bar')}
                  className={`text-xs px-2.5 py-1 rounded-md transition-colors ${historyChartType === 'bar' ? 'bg-white shadow-sm text-gray-900 font-medium' : 'text-gray-500 hover:text-gray-700'}`}
                >
                  Balken
                </button>
                <button
                  onClick={() => setHistoryChartType('line')}
                  className={`text-xs px-2.5 py-1 rounded-md transition-colors ${historyChartType === 'line' ? 'bg-white shadow-sm text-gray-900 font-medium' : 'text-gray-500 hover:text-gray-700'}`}
                >
                  Linie
                </button>
              </div>
              <button onClick={exportChartAsImage} className="btn-secondary text-xs px-2.5 py-1.5" title="Chart als PNG exportieren">
                <Download size={14} />
              </button>
              <select className="input-field text-xs max-w-xs"
                value={historyChartField}
                onChange={(e) => setHistoryChartField(e.target.value)}>
                <option value="">Feld für Diagramm auswählen...</option>
                {[...new Set(history.map((h: any) => h.dataFieldId))].map((fid: any) => {
                  const h = history.find((he: any) => he.dataFieldId === fid);
                  return <option key={fid} value={fid}>{h?.dataField?.name || fid}</option>;
                })}
              </select>
            </div>
          </div>
          {historyChartField ? (
            (() => {
              const fieldData = history.filter((h: any) => h.dataFieldId === historyChartField).sort((a: any, b: any) => a.day.localeCompare(b.day));
              const chartData = fieldData.map((d: any) => ({
                day: d.day?.slice(5) || d.day,
                summe: d.sum || 0,
                wert: parseFloat(d.lastValue) || 0,
              }));
              const isSum = fieldData[0]?.dataField?.aggregationMode === 'SUM';
              const keyData = isSum ? 'summe' : 'wert';
              const keyName = isSum ? 'Tagessumme' : 'Tageswert';
              return chartData.length > 0 ? (
                <div ref={chartExportRef}>
                <ResponsiveContainer width="100%" height={300}>
                  {historyChartType === 'bar' ? (
                    <BarChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="day" tick={{ fontSize: 11 }} stroke="#9ca3af" />
                      <YAxis tick={{ fontSize: 11 }} stroke="#9ca3af" />
                      <Tooltip
                        contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e5e7eb" }}
                        labelStyle={{ fontWeight: 600 }}
                      />
                      <Bar dataKey={keyData} fill="#B70E0C" radius={[4, 4, 0, 0]} name={keyName} />
                    </BarChart>
                  ) : (
                    <LineChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="day" tick={{ fontSize: 11 }} stroke="#9ca3af" />
                      <YAxis tick={{ fontSize: 11 }} stroke="#9ca3af" />
                      <Tooltip
                        contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e5e7eb" }}
                        labelStyle={{ fontWeight: 600 }}
                      />
                      <Line type="monotone" dataKey={keyData} stroke="#B70E0C" strokeWidth={2} dot={{ fill: "#B70E0C", r: 3 }} name={keyName} />
                    </LineChart>
                  )}
                </ResponsiveContainer>
                </div>
              ) : (
                <p className="text-sm text-gray-400 text-center py-8">Keine numerischen Daten für Chart verfügbar</p>
              );
            })()
          ) : (
            <p className="text-sm text-gray-400 text-center py-8">Wähle ein Feld aus, um den Verlauf als Diagramm zu sehen</p>
          )}
        </div>
      )}
    </div>
  );
}
